import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';

import {ClaimType} from '../../+ess/claims/claims.interfaces';
import {ClaimData, ESSService} from '../../+ess/employee-self.service';
import {Destroyable} from '../../destroyable.component';
import {VariableZonesComponent} from '../employees/variable-zone/variable-zones.component';
import {GPTranslateService} from '../localization/translate.service';
import {TreeNode} from '../ui';
import {Variable} from '../workflow/workflow-v2.interface';

@Component({
  selector: 'gp-claim-form',
  templateUrl: './claim-form.component.html',
  styleUrls: ['./claim-form.component.scss']
})
export class ClaimFormComponent extends Destroyable implements OnInit {

  @Input()
  public selectedClaim: ClaimData;

  @Input()
  public claimTypeTree: Array<TreeNode<any>>;

  @Input()
  public readOnly = false;

  @Input()
  public contractId: number;

  @Input()
  public cliRegId: number;

  @Input()
  public claimTypes: ClaimType[];

  @Input()
  public claimForm: UntypedFormGroup;

  @Input()
  public isTaskDescription = false;

  @Input()
  public isEss = true;

  public selectedClaimType: ClaimType;

  public isClaimAvailable = true;

  public claimNameControl: AbstractControl;
  public claimValueControl: AbstractControl;
  public claimRequestLimitControl: AbstractControl;
  public claimUnitTransIdControl: AbstractControl;
  public claimEvTypeControl: AbstractControl;
  public claimTransIdControl: AbstractControl;

  @ViewChild(VariableZonesComponent)
  public variableZones: VariableZonesComponent;

  @Output()
  private requestUpdate: EventEmitter<Variable> = new EventEmitter();

  constructor(private formBuilder: UntypedFormBuilder,
              private translate: GPTranslateService,
              private essService: ESSService) {
    super();
  }

  ngOnInit(): void {
    this.selectedClaimType = this.selectedClaim ? this.getSelectedClaimType(this.selectedClaim.name) : null;
    this.initClaimForm();
    this.claimNameControl.valueChanges.pipe(
      tap((name) => this.selectClaimType(name))
    ).subscribe();
  }

  public isSelectedClaimTypeUnlimited(): boolean {
    return this.isClaimTypeUnlimited(this.selectedClaimType);
  }

  public selectClaimType(name: string) {
    if (this.readOnly) {
      return;
    }
    this.selectedClaimType = this.getSelectedClaimType(name);
    if (this.selectedClaimType) {
      this.claimValueControl.setValue(null);
      this.claimEvTypeControl.setValue(this.selectedClaimType.attributes.type);
      this.claimTransIdControl.setValue(this.selectedClaimType.attributes.transId);
      if (this.isSelectedClaimTypeUnlimited()) {
        this.setUnlimitedClaimTypeFields();
      } else {
        this.setLimitedClaimTypeFields();
      }
    }
  }

  public isFormValid() {
    return this.claimForm.valid
      && this.isClaimAvailable
      && (this.isSelectedClaimTypeUnlimited() || this.claimValueControl.value <= this.claimRequestLimitControl.value)
      && this.variableZones.isFormValid();
  }

  private isClaimTypeUnlimited(claimType: ClaimType): boolean {
    return claimType?.attributes?.payAccuNameThreshold === 'NO_LIMIT';
  }

  private initClaimForm() {
    if (this.selectedClaim) {
      Object.keys(this.claimForm.controls).forEach(key => {
        this.claimForm.get(key).setValue(this.selectedClaim[key]);
      });
      this.selectedClaimType.attributes.values = this.selectedClaim.values;
    }
    this.claimNameControl = this.claimForm.get('name');
    this.claimValueControl = this.claimForm.get('value');
    this.claimUnitTransIdControl = this.claimForm.get('unitTransId');
    this.claimRequestLimitControl = this.claimForm.get('requestLimit');
    this.claimEvTypeControl = this.claimForm.get('evType');
    this.claimTransIdControl = this.claimForm.get('transId');

    this.claimForm.valueChanges.pipe(
      tap((value) => this.requestUpdate.emit({name: 'request', value, scope: 'global'})),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

  }

  public get translatedAmountUnit(): string {
    return this.translate.instant(this.claimUnitTransIdControl?.value);
  }

  public getSelectedClaimTypeName() {
    return this.selectedClaimType ? this.selectedClaimType.attributes.name : null;
  }

  public getSelectedClaimType(claimTypeName) {
    return this.claimTypes.find(claimType => claimType.attributes.name === claimTypeName);
  }

  private getOngoingAccuValue(): Observable<any> {
    if (!this.selectedClaimType.attributes.payAccuNameThreshold) {
      return of({});
    }
    const searchStr = `name=${this.selectedClaimType.attributes.payAccuNameThreshold}`;
    return this.essService.getOngoingAccus(searchStr)
      .pipe(
        tap((accus) => {
          if (accus && accus.data && accus.data.length > 0) {
            this.selectedClaimType.attributes.ongoingAccuValue = accus.data[0].attributes.value;
            this.selectedClaimType.attributes.payAccuUnitTransId = accus.data[0].attributes.unitTransId;
          }
        }));
  }

  private setUnlimitedClaimTypeFields() {
    this.claimUnitTransIdControl.setValue(
      this.selectedClaimType.attributes.unitTransId === 'Amount'
        ? this.selectedClaimType.attributes.currencyString
        : this.selectedClaimType.attributes.unitTransId);
    this.claimRequestLimitControl.setValue(null);
    this.isClaimAvailable = true;
  }

  private setLimitedClaimTypeFields() {
    this.getOngoingAccuValue().pipe(
      tap(() => {
        this.isClaimAvailable = !!this.selectedClaimType.attributes.ongoingAccuValue;
        if (this.isClaimAvailable) {
          this.claimUnitTransIdControl.setValue(
            this.selectedClaimType.attributes.unitTransId === 'Amount' &&
            this.selectedClaimType.attributes.payAccuUnitTransId === 'Amount'
              ? this.selectedClaimType.attributes.currencyString
              : this.selectedClaimType.attributes.unitTransId);
          this.claimRequestLimitControl.setValue(this.selectedClaimType.attributes.ongoingAccuValue);
        } else {
          this.claimRequestLimitControl.setValue(null);
          this.claimUnitTransIdControl.setValue(null);
        }
      })
    ).subscribe();
  }

}
